import AdminDivisionsCamera from '@/data/mapping-camera-comuni7917.json';
import {
  getCoalitionColor,
  getFullNameCandidate,
  getMostraDettaglio,
  getName,
  getPartyColor,
} from '@/lib/candidatesHelpers';
import { convertIdComune, convertIdProvince } from '@/lib/convertId';
import { formatNumber, parsePercentage } from '@/lib/helpers';
import { getLevelSettings } from '@/lib/levelsHelpers';
import {
  AutocompleteOption,
  DataLevel,
  DynamicTurnout,
  Region,
  Result,
  ResultAPI,
  ResultCodes,
  Year,
} from '@/lib/model';
import {
  getAffluenzaByLivelloEleT,
  getAffluenzaByLivelloPerc,
  getAffluenzaByLivelloVoti,
} from './affluenzaHelper';

const API_URL = `https://2023-real-time-data.s3.eu-central-1.amazonaws.com/`;

let fetchController = new AbortController();
let fetchDynamicTurnoutController = new AbortController();

export const parseResult = (
  result: ResultAPI,
  year: Year,
  region: Region,
  level: DataLevel,
  dynamicTurnout: DynamicTurnout[]
) => {
  return {
    level,
    year,
    region,
    placeName:
      (level === DataLevel.MAIN || level === DataLevel.REGIONE
        ? result.int.desc_reg
        : level === DataLevel.PROVINCIA
        ? result.int.desc_prov
        : result.int.desc_com) || '',
    showVoti: true,
    scrutinized: {
      current: result.int.sz_perv || result.int.sz_pres || 0,
      total: result.int.sz_tot || 0,
    },
    coalitions: result.cand
      .map(coalition => ({
        coalitionId: coalition.pos,
        label: getName(year, region, true, coalition),
        fullName: getFullNameCandidate(
          year,
          region,
          true,
          coalition,
          result.isMultiCollegio
        ),
        votes: formatNumber(coalition.voti_uni || coalition.voti || 0),
        percentage: parsePercentage(
          coalition.perc_uni || coalition.perc || '0'
        ),
        fillColor: getCoalitionColor(year, region, level, coalition),
        candidates: getMostraDettaglio(year, region, coalition.pos)
          ? coalition.liste
              ?.map(candidate => ({
                coalitionId: coalition.pos,
                candidateId: candidate.pos,
                label: getName(year, region, false, candidate), // change to this when in PROD -> list.desc_lis,
                fullName: getFullNameCandidate(
                  year,
                  region,
                  false,
                  candidate,
                  result.isMultiCollegio
                ),
                votes: formatNumber(candidate.voti),
                percentage: parsePercentage(candidate.perc),
                fillColor: getPartyColor(year, region, candidate.pos),
              }))
              .sort(({ percentage: a }, { percentage: b }) => b - a)
          : [],
      }))
      .sort(({ percentage: a }, { percentage: b }) => b - a),
    idCodes: {
      codReg: `${result.int.cod_reg}`,
      codCirc: `${result.int.cod_circ}`,
      codProv: `${result.int.cod_prov}`,
      codClPlu: `${result.int.cod_plu}`,
      codClUni: `${result.int.cod_cl_uni}`,
      codCom: `${result.int.cod_com}`,
    },
    summary: {
      turnout: {
        label: 'Affluenza',
        value:
          Year.Y2023 === year
            ? `${getAffluenzaByLivelloPerc(
                dynamicTurnout,
                level,
                result.int.cod_reg,
                result.int.cod_prov,
                result.int.cod_com
              )}%`
            : `${parsePercentage(result.int.perc_vot).toFixed(2)}%`,
      },
      totalElectors: {
        label: 'Totale Elettori',
        value:
          Year.Y2023 === year
            ? formatNumber(
                getAffluenzaByLivelloEleT(
                  dynamicTurnout,
                  level,
                  result.int.cod_reg,
                  result.int.cod_prov,
                  result.int.cod_com
                )
              )
            : formatNumber(result.int.ele_t),
      },
      totalVoters: {
        label: 'Totale Votanti',
        value:
          Year.Y2023 === year
            ? formatNumber(
                getAffluenzaByLivelloVoti(
                  dynamicTurnout,
                  level,
                  result.int.cod_reg,
                  result.int.cod_prov,
                  result.int.cod_com
                )
              )
            : formatNumber(result.int.vot_t),
      },
      blankCards: {
        label: 'Schede Bianche',
        value: formatNumber(result.int.sk_bianche),
      },
      contestedCards: {
        label: 'Schede Contestate',
        value:
          Year.Y2018 === year ? '-1' : formatNumber(result.int.sk_contestate),
      },
      nullCards: {
        label:
          Year.Y2018 === year ? 'Schede Nulle o Contestate' : 'Schede Nulle',
        value: formatNumber(result.int.sk_nulle),
      },
    },
  };
};

export const parseResultToMapLayer = (
  result: ResultAPI,
  year: Year,
  region: Region,
  level: DataLevel
) => {
  const { variable } = getLevelSettings(level);

  const winningCandidate = result.cand.sort((a, b) => {
    const aPerc = parsePercentage(a.perc_uni || a.perc || '0');
    const bPerc = parsePercentage(b.perc_uni || b.perc || '0');

    return bPerc - aPerc;
  })[0];

  const votes = winningCandidate?.voti_uni || winningCandidate?.voti || 0;
  const perc = winningCandidate?.perc_uni || winningCandidate?.perc || null;

  let color = '#D9D9D9'; // default se voti 0 o perc null
  if (winningCandidate === undefined) {
    console.log('check', result);
  } else if (votes > 0 && perc !== null) {
    color = getCoalitionColor(year, region, level, winningCandidate);
  }

  let idFromVariable: string = result.int[variable] as string; // default
  if (level === DataLevel.PROVINCIA) {
    idFromVariable = convertIdProvince(
      `${result.int.cod_reg}`,
      `${result.int.cod_prov}`
    );
  } else if (level === DataLevel.COMUNE) {
    idFromVariable = convertIdComune(
      `${result.int.cod_reg}`,
      `${result.int.cod_prov}`,
      `${result.int.cod_com}`
    );
  }

  return {
    fillColor: color,
    opacity: 1, // full color of the winning coalition // parsePercentage(winningCandidate?.perc) / 100,
    layerId: idFromVariable,
    coordinates: [],
  };
};

const fetchResults = async (
  abort: boolean,
  year: Year,
  region: Region,
  level: DataLevel,
  codes?: ResultCodes
): Promise<ResultAPI | ResultAPI[] | null> => {
  let yearDirectory = '';
  switch (year) {
    case Year.Y2018:
      yearDirectory = '2018/';
      break;
    case Year.Y2022:
      yearDirectory = '2022/';
      break;
    case Year.Y2023:
    default:
      yearDirectory = '2023/';
      break;
  }

  const typeDirectory = '';
  const regionDirectory = region === Region.LOMBARDIA ? '03' : '12';

  let url = `${API_URL}${yearDirectory}${typeDirectory}07${regionDirectory}`;

  if (level === DataLevel.PROVINCIA) {
    url += 'prov';
  } else if (level === DataLevel.COMUNE) {
    if (
      codes === undefined ||
      (codes !== undefined && codes.codProv === undefined)
    ) {
      url += 'comuni_all_4';
    } else if (
      codes !== undefined &&
      codes.codProv !== undefined &&
      codes.codProv.length === 2
    ) {
      url += `0${codes?.codProv}`; // if cod prov 78
    } else {
      url += `${codes?.codProv}`; // if cod prov 104
    }
  }

  url += '.json';

  try {
    if (abort) {
      fetchController.abort();
      fetchController = new AbortController();
    }

    let res: ResultAPI | ResultAPI[] = await (
      await fetch(url, {
        signal: fetchController.signal,
      })
    )
      .json()
      .catch(_ => {
        return null;
      });

    return res;
  } catch {
    return null;
  }
};

export const getDynamicTurnout = async () => {
  try {
    fetchDynamicTurnoutController.abort();
    fetchDynamicTurnoutController = new AbortController();

    const dynamicTurnout = await (
      await fetch(`${API_URL}2023/votanti/affluenza.json`, {
        signal: fetchDynamicTurnoutController.signal,
      })
    ).json();

    return dynamicTurnout;
  } catch (e) {
    return [];
  }
};

export const getResults = async (
  abort: boolean,
  year: Year,
  region: Region,
  level: DataLevel,
  dynamicTurnout: DynamicTurnout[],
  codes?: ResultCodes
): Promise<Result[]> => {
  try {
    let data = await fetchResults(abort, year, region, level, codes);
    data = !data ? [] : Array.isArray(data) ? data : [data];

    return data.map((result: ResultAPI) => ({
      ...parseResult(result, year, region, level, dynamicTurnout),
      mapLayer: parseResultToMapLayer(result, year, region, level),
    }));
  } catch (e) {
    console.log('error fetching results', e);

    return [];
  }
};

export const getAutocompleteOptions = () => {
  const data = AdminDivisionsCamera as AutocompleteOption[];

  return data
    .reduce((acc: AutocompleteOption[], el) => {
      if (
        acc.some(
          option =>
            option.cod_comune === el.cod_comune &&
            option.cod_provincia === el.cod_provincia
        ) ||
        // Limit data to Lombardia and Lazio
        (el.cod_regione !== '3' && el.cod_regione !== '12')
      ) {
        return acc;
      }

      return [...acc, el];
    }, [])
    .sort((a, b) => {
      // should be:
      // a.cod_provincia.localeCompare(b.cod_provincia);

      return a.nome_provincia.localeCompare(b.nome_provincia);
    });
};

export const getLastVersion = async () => {
  return await (
    await fetch(
      `https://2023-real-time-data.s3.eu-central-1.amazonaws.com/2023/last_version.json`
    )
  ).json();
};
